import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Edit, User, UserCog } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import Layout from "@/components/Layout";
import { Profile, UserRole } from "@/types/user";
import CreateUserDialog from "@/components/admin/users/CreateUserDialog";
import useAdminStatus from "@/hooks/useAdminStatus";
import { getAllUserProfiles } from "@/services/user";

const UsersManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<Profile[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const { isAdmin, isLoading: adminLoading } = useAdminStatus();
  
  useEffect(() => {
    // Check if user is admin
    if (!adminLoading && isAdmin === false) {
      toast({
        title: "Unauthorized",
        description: "You don't have permission to access this page.",
        variant: "destructive"
      });
      navigate("/dashboard");
    } else if (!adminLoading && isAdmin === true) {
      loadUsers();
    }
  }, [adminLoading, isAdmin, navigate]);
  
  const loadUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });
        
      if (error) throw error;
      
      // Type cast each user's role to UserRole
      const typedUsers = (data || []).map(user => ({
        ...user,
        role: user.role as UserRole
      }));
      
      setUsers(typedUsers);
    } catch (error) {
      console.error("Error loading users:", error);
      toast({
        title: "Error",
        description: "Failed to load users",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const getRoleBadgeVariant = (role: UserRole) => {
    switch (role) {
      case "admin": return "destructive";
      case "instructor": return "default";
      case "learner": return "secondary";
      default: return "outline";
    }
  };
  
  const handleUserCreated = () => {
    loadUsers();
    setCreateDialogOpen(false);
  };

  const handleEditUser = (userId: string) => {
    navigate(`/admin/users/edit/${userId}`);
  };

  if (adminLoading) {
    return (
      <Layout>
        <div className="container mx-auto py-8 flex justify-center items-center">
          <div className="text-center">
            <div className="h-12 w-12 border-4 border-t-primary border-r-primary/30 border-b-primary/30 border-l-primary/30 rounded-full animate-spin mx-auto mb-4"></div>
            <p>Verifying admin permissions...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto py-8 px-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>User Management</CardTitle>
              <CardDescription>Manage all users in the system</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => navigate("/admin/users/admin-privileges")}
              >
                <UserCog className="mr-2 h-4 w-4" />
                Admin Privileges
              </Button>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                Create User
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="h-12 w-12 border-4 border-t-primary border-r-primary/30 border-b-primary/30 border-l-primary/30 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading users...</p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-4">
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">
                            {user.first_name} {user.last_name}
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.title || "—"}</TableCell>
                          <TableCell>{user.company || "—"}</TableCell>
                          <TableCell>
                            <Badge variant={getRoleBadgeVariant(user.role as UserRole)}>
                              {user.role || "learner"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button 
                              variant="outline" 
                              size="icon"
                              onClick={() => handleEditUser(user.id)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <CreateUserDialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleUserCreated}
      />
    </Layout>
  );
};

export default UsersManagementPage;
